import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bell, X, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import {
    markNotificationRead,
    removeNotification,
    clearAllNotifications,
    markAllNotificationsRead,
    consumePendingRewardNotifications,
    acknowledgeRewardNotifications,
} from '../redux/actions/backgroundProcesses';
import { fetchPendingRewardNotifications } from '../api/professoru/forum.api';
import './NotificationCenter.css';
import { getFinalMasteredData, getFinalMixedData, getPreviewData, retrieveMixPreview } from '../redux/actions/audioEngineer';

export const NotificationCenter = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation()
    const [isOpen, setIsOpen] = useState(false);
    const bellRef = useRef(null);
    const [dropdownTop, setDropdownTop] = useState(0);
    const { mixReportId } = useSelector((state) => state.audioEngineer);

    const { processes, notifications, completedProcesses } = useSelector(
        (state) => state.backgroundProcesses || {}
    );

    const normalizePath = (path) => (path || '').split('?')[0].replace(/\/$/, '');
    const currentPath = normalizePath(location.pathname);
    const isMatchingRoute = (redirectPath) => {
        if (!redirectPath) return false;
        try {
            return normalizePath(new URL(redirectPath).pathname) === currentPath;
        } catch {
            return normalizePath(redirectPath) === currentPath;
        }
    };

    const visibleNotifications = (notifications || []).filter(
        (n) => !isMatchingRoute(n.redirectPath)
    );

    const unreadCount = visibleNotifications.filter(n => !n.read).length || 0;

    useEffect(() => {
        const matching = (notifications || []).filter((n) => isMatchingRoute(n.redirectPath));
        if (!matching.length) return;

        matching.forEach((n) => {
            dispatch(removeNotification(n.id));
        });
        dispatch(acknowledgeRewardNotifications(matching));
    }, [notifications, currentPath, dispatch]);

    useEffect(() => {
        let cancelled = false;

        const syncPendingRewardNotifications = async () => {
            try {
                const pending = await fetchPendingRewardNotifications();
                if (!cancelled && pending?.length) {
                    dispatch(consumePendingRewardNotifications(pending));
                }
            } catch (error) {
                console.error('Failed to fetch pending reward notifications', error);
            }
        };

        syncPendingRewardNotifications();
        const intervalId = setInterval(syncPendingRewardNotifications, 30000);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [dispatch]);

    const handleNotificationClick = (notification) => {
        const clickedProcess = completedProcesses.find(p => p.id == notification.processId)
        dispatch(markNotificationRead(notification.id));
        dispatch(removeNotification(notification.id));
        dispatch(acknowledgeRewardNotifications([notification]));
        if (notification.redirectPath) {
            if (clickedProcess?.metadata?.dataType === 'mastered-preview' && clickedProcess.metadata?.ids) {
                dispatch(getPreviewData(clickedProcess.metadata?.ids || []))
            }
            if (clickedProcess?.metadata?.dataType === 'mastered-final' && clickedProcess.metadata?.ids) {
                dispatch(getFinalMasteredData(clickedProcess.metadata?.ids || []))
            }
            if (clickedProcess?.metadata?.dataType === 'mix-preview' && clickedProcess.metadata?.body?.taskId) {
                dispatch(retrieveMixPreview({taskId: clickedProcess.metadata?.body?.taskId}, () => {}, false))
            }
            if (clickedProcess?.metadata?.dataType === 'mix-final' && clickedProcess.metadata?.ids) {
                dispatch(getFinalMixedData({taskId: clickedProcess.metadata?.ids}, () => {}, false))
            }
            navigate(notification.redirectPath);
            setIsOpen(false);
        } else if (clickedProcess?.metadata?.dataType === 'mix-report' && mixReportId) {
            console.log('navigating to mix report', mixReportId)
            navigate(`/admin/urstudio/analysis-report?id=${mixReportId}`)
        }
    };

    const handleRemoveNotification = (notification, e) => {
        e.stopPropagation();
        dispatch(removeNotification(notification.id));
        dispatch(acknowledgeRewardNotifications([notification]));
    };

    const handleClearAll = () => {
        dispatch(acknowledgeRewardNotifications(visibleNotifications));
        dispatch(clearAllNotifications());
    };

    const handleMarkAllRead = () => {
        dispatch(acknowledgeRewardNotifications(visibleNotifications));
        dispatch(markAllNotificationsRead());
    };

    // the dropdown is portalled to <body>, so anchor it to the bell manually
    const updateDropdownPosition = useCallback(() => {
        const rect = bellRef.current?.getBoundingClientRect();
        if (rect) setDropdownTop(rect.bottom + 8);
    }, []);

    useLayoutEffect(() => {
        if (!isOpen) return;

        updateDropdownPosition();
        window.addEventListener('resize', updateDropdownPosition);
        window.addEventListener('scroll', updateDropdownPosition, true);

        return () => {
            window.removeEventListener('resize', updateDropdownPosition);
            window.removeEventListener('scroll', updateDropdownPosition, true);
        };
    }, [isOpen, updateDropdownPosition]);

    useEffect(() => {
        // Add/remove class to body when notification opens/closes on mobile
        if (isOpen && window.innerWidth <= 768) {
            document.body.classList.add('notification-open');
        } else {
            document.body.classList.remove('notification-open');
        }
    }, [isOpen]);

    return (
        <div className="notification-center">
            {/* Notification Bell Icon - typically placed in Header */}
            <button
                ref={bellRef}
                className="notification-bell"
                onClick={() => setIsOpen(!isOpen)}
                title="Notifications"
            >
                <Bell size={20} />
                {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount}</span>
                )}
            </button>

            {/* Backdrop + dropdown are portalled to <body> so they are never clipped
                or painted under stacking contexts created by page sections */}
            {isOpen && createPortal(
                <>
                    {/* Backdrop Overlay - only show on mobile */}
                    <div
                        className="notification-backdrop"
                        onClick={() => setIsOpen(false)}
                    />

                    {/* Notification Dropdown Panel */}
                <div className="notification-dropdown" style={{ top: `${dropdownTop}px` }}>
                    {/* Header */}
                    <div className="notification-header">
                        <h3>Notifications</h3>
                        <button
                            className="close-btn"
                            onClick={() => setIsOpen(false)}
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Active Processes Section */}
                    {/* {processes && processes.length > 0 && (
                        <div className="notification-section">
                            <h4 className="section-title">Active Processes</h4>
                            <div className="processes-list">
                                {processes.map((process) => (
                                    <div key={process.id} className="process-item active">
                                        <div className="process-icon">
                                            <Loader size={16} className="spinner" />
                                        </div>
                                        <div className="process-content">
                                            <p className="process-name">{process.name}</p>
                                            <div className="progress-bar">
                                                <div
                                                    className="progress-fill"
                                                    style={{ width: `${process.progress || 0}%` }}
                                                />
                                            </div>
                                            <p className="progress-text">{process.progress || 0}%</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )} */}

                    {/* Notifications Section */}
                    {visibleNotifications && visibleNotifications.length > 0 ? (
                        <>
                            <div className="notification-section">
                                <div className="section-header">
                                    <h4 className="section-title">Completed</h4>
                                    {unreadCount > 0 && (
                                        <button
                                            className="mark-all-link"
                                            onClick={handleMarkAllRead}
                                        >
                                            Mark all as read
                                        </button>
                                    )}
                                </div>
                                <div className="notifications-list">
                                    {[...visibleNotifications].sort((a, b) => (a.read === b.read ? 0 : a.read ? 1 : -1)).map((notification) => (
                                        <div
                                            key={notification.id}
                                            className={`notification-item ${notification.type
                                                } ${notification.read ? 'read' : 'unread'}`}
                                            onClick={() => handleNotificationClick(notification)}
                                        >
                                            <div className="notification-icon">
                                                {notification.type === 'success' ? (
                                                    <CheckCircle size={16} className="success-icon" />
                                                ) : (
                                                    <AlertCircle size={16} className="error-icon" />
                                                )}
                                            </div>
                                            <div className="notification-content">
                                                <p className="notification-message">
                                                    {notification.message}
                                                </p>
                                                <p className="notification-time">
                                                    {formatTime(notification.createdAt)}
                                                </p>
                                            </div>
                                            <button
                                                className="remove-notification-btn"
                                                onClick={(e) =>
                                                    handleRemoveNotification(notification, e)
                                                }
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {visibleNotifications.length > 0 && (
                                <div className="notification-footer">
                                    <button
                                        className="clear-all-btn"
                                        onClick={handleClearAll}
                                    >
                                        Clear All
                                    </button>
                                </div>
                            )}
                        </>
                    ) : processes && processes.length === 0 ? (
                        <div className="empty-state">
                            <p>No notifications</p>
                        </div>
                    ) : null}
                </div>
                </>,
                document.body
            )}
        </div>
    );
};

/**
 * Utility function to format notification time
 */
const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
};

export default NotificationCenter;
